import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';
import { ColorNeutralBlack, ColorNeutralWhite } from 'autogen/design-tokens/tokens';
import type { ConnectedContentDebugInfoMaybe } from 'autogen/swagger/all';
import {
    DebugActivePeriod,
    DebugAdmin,
    DebugAdminLink,
    DebugPlacements,
    DebugPreviewActivePeriod,
    DebugPreviewInfo
} from 'autogen/translation-keys/trans-website-connectedcontent';
import { Ending, Starting, Time } from 'autogen/translation-keys/trans-website-shared';
import { format } from 'modules/helpers/strings';

const DebugLabel = styled.div`
    position: absolute;
    top: 0%;
    left: 0;
    z-index: 2;
    padding: 3px 6px;
    background-color: #4caf50;
    color: ${ColorNeutralWhite};
    cursor: pointer;
    border-top-left-radius: 6px;
`;

const StyledTable = styled.table`
    td,
    th {
        padding: 8px;
        border-bottom: 1px solid #b8b8b8;
        width: auto;
    }
`;

const StyledTippy = styled(Tippy)`
    &.tippy-box {
        background-color: ${ColorNeutralWhite};
        color: ${ColorNeutralBlack};
    }
`;

export default function DebugInfo({ debugInfo }: { debugInfo: ConnectedContentDebugInfoMaybe }) {
    const { t } = useTranslation();

    const content = (
        <StyledTable>
            <thead>
                <tr>
                    <th>{t(DebugPlacements)}</th>
                    {!!debugInfo.activePeriod.activeFromDate && !!debugInfo.activePeriod.activeToDate && (
                        <th>{t(DebugActivePeriod)}</th>
                    )}
                    {!!debugInfo.previewActivePeriod &&
                        !!debugInfo.previewActivePeriod.activeFromDate &&
                        !!debugInfo.previewActivePeriod.activeToDate && <th>{t(DebugPreviewActivePeriod)}</th>}
                    {!!debugInfo.previewInformation && <th>{t(DebugPreviewInfo)}</th>}
                    <th>{t(DebugAdmin)}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>
                        {debugInfo.placements.map((placement, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={i}>{placement}</li>
                        ))}
                    </td>
                    {!!debugInfo.activePeriod.activeFromDate && !!debugInfo.activePeriod.activeToDate && (
                        <td>
                            {t(Starting)}{' '}
                            {Intl.DateTimeFormat('da-DK', { dateStyle: 'short' }).format(
                                new Date(debugInfo.activePeriod.activeFromDate)
                            )}{' '}
                            {format(t(Time))(
                                Intl.DateTimeFormat('da-DK', { timeStyle: 'short' }).format(
                                    new Date(debugInfo.activePeriod.activeFromDate)
                                )
                            )}
                            <br />
                            {t(Ending)}{' '}
                            {Intl.DateTimeFormat('da-DK', { dateStyle: 'short' }).format(
                                new Date(debugInfo.activePeriod.activeToDate)
                            )}{' '}
                            {format(t(Time))(
                                Intl.DateTimeFormat('da-DK', { timeStyle: 'short' }).format(
                                    new Date(debugInfo.activePeriod.activeToDate)
                                )
                            )}
                        </td>
                    )}
                    {!!debugInfo.previewActivePeriod &&
                        !!debugInfo.previewActivePeriod.activeFromDate &&
                        !!debugInfo.previewActivePeriod.activeToDate && (
                            <td>
                                {Intl.DateTimeFormat('da-DK', { dateStyle: 'short' }).format(
                                    new Date(debugInfo.previewActivePeriod.activeFromDate)
                                )}
                                {' - '}
                                {Intl.DateTimeFormat('da-DK', { dateStyle: 'short' }).format(
                                    new Date(debugInfo.previewActivePeriod.activeToDate)
                                )}
                            </td>
                        )}
                    {!!debugInfo.previewInformation && (
                        <td>
                            {debugInfo.previewInformation.map((line, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <li key={i}>{line}</li>
                            ))}
                        </td>
                    )}
                    <td>
                        <a href={debugInfo.adminLink} target="_blank" rel="noopener noreferrer">
                            {t(DebugAdminLink)}
                        </a>
                    </td>
                </tr>
            </tbody>
        </StyledTable>
    );

    return (
        <StyledTippy
            content={content}
            arrow={false}
            maxWidth="100%"
            interactive
            placement="bottom"
            appendTo={() => document.body}
            animation={false}
        >
            <DebugLabel>Info</DebugLabel>
        </StyledTippy>
    );
}
